import * as THREE from 'three'
import { Pane } from 'tweakpane'

import Time from './Utils/Time.js'
import Sizes from './Utils/Sizes.js'
import Stats from './Utils/Stats.js'

import Resources from './Resources.js'
import Renderer from './Renderer.js'
import Camera from './Camera.js'
import World from './World.js'
import Navigation from './Navigation.js'
import Interaction from './Interaction.js'

import assets from './assets.js'

export default class Experience
{
    static instance

    constructor(_options = {})
    {
        if(Experience.instance)
        {
            return Experience.instance
        }
        Experience.instance = this

        // Options
        this.targetElement = _options.targetElement

        if(!this.targetElement)
        {
            console.warn('Missing \'targetElement\' property')
            return
        }

        this.time = new Time()
        this.sizes = new Sizes()
        this.setConfig()
        this.setStats()
        this.setDebug()
        this.setScene()
        this.setCamera()
        this.setRenderer()
        this.setResources()
        this.setNavigation()
        this.setInteraction()
        this.settingsReady = false
        this.pendingSettings = null
        this.isApplyingSettings = false
        
        // 异步获取项目配置后再初始化世界（因为世界里的视频需要配置）
        this.projectsConfig = null
        this.fetchProjectsConfig().finally(() => {
            this.setWorld()
        })

        this.sizes.on('resize', () =>
        {
            this.resize()
        })

        this.update()
    }

    // static getInstance(_options = {})
    // {
    //     console.log(Experience.instance)
    //     if(Experience.instance)
    //     {
    //         return Experience.instance
    //     }
        
    //     console.log('create')
    //     Experience.instance = new Experience(_options)
        
    //     return Experience.instance
    // }

    setConfig()
    {
        this.config = {}
    
        // Pixel ratio
        this.config.pixelRatio = Math.min(Math.max(window.devicePixelRatio, 1), 2)

        // Width and height
        const boundings = this.targetElement.getBoundingClientRect()
        this.config.width = boundings.width
        this.config.height = boundings.height || window.innerHeight
        this.config.smallestSide = Math.min(this.config.width, this.config.height)
        this.config.largestSide = Math.max(this.config.width, this.config.height)
        
        // Debug：仅控制是否创建 Stats/调试面板，默认不显示，由 F12 面板开关控制
        this.config.debug = this.config.width > 420
    }

    async fetchProjectsConfig()
    {
        try {
            const response = await fetch('/api/projects')
            this.projectsConfig = await response.json()
        } catch (err) {
            console.warn('Failed to fetch projects config:', err)
        }
    }

    setStats()
    {
        if(this.config.debug)
        {
            this.stats = new Stats(false)
        }
    }

    setDebug()
    {
        if(this.config.debug)
        {
            this.debug = new Pane({ title: '设置' })
            this.debug.containerElem_.style.width = '320px'
            this.debug.containerElem_.style.display = 'none'

            this.debug.on('change', () => {
                if (this.isApplyingSettings) return
                if (typeof this.onSettingsChange === 'function') {
                    this.onSettingsChange()
                }
            })
        }
    }

    setDebugPanelVisible(visible)
    {
        if(this.stats)
        {
            if(visible) this.stats.activate()
            else this.stats.deactivate()
        }
        if(this.debug && this.debug.containerElem_)
        {
            this.debug.containerElem_.style.display = visible ? 'block' : 'none'
        }
    }

    getSettings()
    {
        if (this.debug) {
            return this.debug.exportPreset()
        }
        return null
    }

    setSettings(data)
    {
        if (!data) return
        if (!this.settingsReady) {
            this.pendingSettings = data
            return
        }
        if (this.debug) {
            this.isApplyingSettings = true
            this.debug.importPreset(data)
            setTimeout(() => { this.isApplyingSettings = false }, 0)
        }
    }
    
    setScene()
    {
        this.scene = new THREE.Scene()
    }

    setCamera()
    {
        this.camera = new Camera()
    }

    setRenderer()
    {
        this.renderer = new Renderer({ rendererInstance: this.rendererInstance })

        this.targetElement.appendChild(this.renderer.instance.domElement)
    }

    setResources()
    {
        this.resources = new Resources(assets)
        this.resources.on('end', () =>
        {
            this.settingsReady = true
            if (this.pendingSettings)
            {
                const pending = this.pendingSettings
                this.pendingSettings = null
                this.setSettings(pending)
            }
        })
    }

    setWorld()
    {
        this.world = new World()
    }

    setNavigation()
    {
        this.navigation = new Navigation()
    }

    setInteraction()
    {
        this.interaction = new Interaction()
    }

    update()
    {
        if(this.stats)
            this.stats.update()
        
        this.camera.update()
        
        if(this.renderer)
            this.renderer.update()

        if(this.world)
            this.world.update()

        if(this.navigation)
            this.navigation.update()

        if(this.interaction)
            this.interaction.update()

        window.requestAnimationFrame(() =>
        {
            this.update()
        })
    }

    resize()
    {
        // Config
        const boundings = this.targetElement.getBoundingClientRect()
        this.config.width = boundings.width
        this.config.height = boundings.height
        this.config.smallestSide = Math.min(this.config.width, this.config.height)
        this.config.largestSide = Math.max(this.config.width, this.config.height)

        this.config.pixelRatio = Math.min(Math.max(window.devicePixelRatio, 1), 2)

        if(this.camera)
            this.camera.resize()

        if(this.renderer)
            this.renderer.resize()

        if(this.world)
            this.world.resize()
    }

    destroy()
    {
        
    }
}
